import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { recalculateOrder, resLineToCart } from "~/helpers/cartHelpers";
import SiteContext from "./SiteContext";
import {
  CartLine,
  DeliveryInfo,
  OrderSource,
  OrderType,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
} from "~/entities/orderEntities";
import MenuContext from "./MenuContext";
import { EMPTY_CUSTOMER, EMPTY_ORDER_RESPONSE } from "~/api/endpoints/Order";
import TaxContext from "~/contexts/TaxContext";
import {
  getOrderType,
  getPromotionsRequiringSelection,
  PromotionRequiringSelection,
} from "~/helpers/orderHelper";
import { BaseUser } from "~/entities/authEntities";
import { getRouteEnd, urlGen } from "~/helpers/routesHelpers";
import { KEYS } from "~/modules/checkout/constants";
import PromotionContext from "~/contexts/PromotionContextProvider";
import ChangeOrderModal from "~/modules/order/components/ChangeOrderModal/ChangeOrderModal";
import AuthContext from "./AuthContext";
import ConfirmActionModal from "~/modules/order/components/ConfirmActionModal/ConfirmActionModal";
import { getOptionalJSONNullStrFormValue } from "~/helpers/formHelpers";

const OrderContext = createContext<{
  orderSummary: ResponseOrderWithCartLines;
  orderSource: OrderSource;
  orderTypeCode: string;
  orderType?: OrderType;
  deliveryInfo: DeliveryInfo | null;
  isTableOrdering: boolean;
  setTableNumber: (value: string) => void;
  getOrderLineById: (id: string) => CartLine | null | undefined;
  addOrderLine: (line: CartLine) => void;
  removeOrderLine: (id: string) => void;
  updateOrderLine: (line: CartLine) => void;
  reset: () => void;
  refresh: () => void;
  setCustomerData: (x: BaseUser, allowSavePersonalData?: boolean) => void;
  redeemPromotion: (key: string) => void;
  removePromotion: () => void;
  redeemPromotionCode: (code: string) => void;
  removePromotionCode: () => void;
  setOrderTypeInfo: (
    orderType: OrderType,
    reqTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    resetOrder: boolean,
  ) => void;
  numTimeSlots: number;
  successPageOrderInfo: ResponseOrderWithLines | null;
  openOrderTimeModal: VoidFunction;
  promotionsRequiringSelection: PromotionRequiringSelection[];
  setGroupOrder: (val: boolean, redirectToPayment: boolean) => void;
  orderLoading: boolean;
  setConfirmingAction: (x: ConfirmingAction | null) => void;
  readonlyCart: boolean;
  asapWait: number | null;
  signBackInRequired: boolean;
  sessionRequestedTime: string | null | undefined;
}>({
  orderSummary: EMPTY_ORDER_RESPONSE,
  orderSource: OrderSource.ONLINE,
  orderTypeCode: "",
  deliveryInfo: null,
  isTableOrdering: false,
  setTableNumber: () => null,
  getOrderLineById: () => null,
  addOrderLine: () => null,
  removeOrderLine: () => null,
  updateOrderLine: () => null,
  reset: () => null,
  refresh: () => null,
  setCustomerData: () => null,
  redeemPromotion: () => null,
  removePromotion: () => null,
  redeemPromotionCode: () => null,
  removePromotionCode: () => null,
  setOrderTypeInfo: () => null,
  numTimeSlots: 0,
  successPageOrderInfo: null,
  openOrderTimeModal: () => null,
  promotionsRequiringSelection: [],
  setGroupOrder: () => null,
  orderLoading: true,
  setConfirmingAction: () => null,
  readonlyCart: true,
  asapWait: null,
  signBackInRequired: false,
  sessionRequestedTime: null,
});

export interface ConfirmingAction {
  action: () => any;
  icon: () => JSX.Element;
  title: string;
  body: string;
}

export const OrderContextProvider: React.FC<
  PropsWithChildren<{
    orderInfo: ResponseOrderWithLines | null;
    clearOrder: boolean;
    orderSource: OrderSource;
    orderTypeCode: string;
    deliveryInfo: DeliveryInfo | null;
    paramOrderSource: string;
    paramMenuCode?: string;
    availableTimeSlots: (string | null)[];
    requestedReadyTime?: string | null;
    tableNumber?: string | null;
    savedCustomer?: BaseUser;
    timeValid: boolean;
    asapWait: number | null;
    signBackInRequired: boolean;
    sessionRequestedTime: string | null | undefined;
    asapAvailable: boolean;
  }>
> = ({
  children,
  savedCustomer,
  orderInfo: unclearedOrderInfo,
  clearOrder,
  orderSource,
  orderTypeCode: loaderOrderTypeCode,
  deliveryInfo: loaderDeliveryInfo,
  paramOrderSource,
  paramMenuCode,
  availableTimeSlots,
  requestedReadyTime,
  tableNumber,
  timeValid,
  asapWait,
  signBackInRequired,
  sessionRequestedTime,
  asapAvailable,
}) => {
  const { scheduledPromotions } = useContext(PromotionContext);
  const [showOrderTimeModal, setShowOrderTimeModal] = useState(false);
  const [confirmingAction, setConfirmingAction] =
    useState<ConfirmingAction | null>(null);
  const { site, orgId } = useContext(SiteContext);
  const { taxFromCode } = useContext(TaxContext);
  const { menu } = useContext(MenuContext);
  const { useComoAuth } = useContext(AuthContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher({ key: KEYS.SUBMIT_ORDER_FETCHER });

  const { table: paramTableNumber } = useParams();

  const isTableOrdering = orderSource === OrderSource.TABLE_ORDERING;
  const defaultTableNumber = paramTableNumber ?? tableNumber;

  // Get the order type code from the fetcher request data, but only for requests where it is specified
  // (i.e. requests to change order type)
  const orderTypeCode = useMemo(() => {
    if (fetcher.state === "idle") {
      // When fetcher state is idle however, force getting the value from the loader, to prevent issues where the session is
      // not getting set correctly from being hidden / harder to reproduce.
      return loaderOrderTypeCode;
    } else {
      return (
        fetcher.formData?.get("orderTypeCode")?.toString() ??
        loaderOrderTypeCode
      );
    }
  }, [loaderOrderTypeCode, fetcher.state, fetcher.formData]);

  const orderSummary: ResponseOrderWithCartLines = useMemo(() => {
    let useLoaderOrderInfo = false;
    let actualClearOrder = clearOrder;
    let currentlySettingRequestedTime: string | null | undefined;

    if (fetcher.state === "idle") {
      // Loader data is up to date - get data from that
      useLoaderOrderInfo = true;
    } else if (fetcher.formData?.get("isReset")) {
      // If currently resetting the order, override clearOrder to true
      useLoaderOrderInfo = true;
      actualClearOrder = true;
      // Get requested time currently being set (when being set), so changes to requested ready time can optimistically update
      currentlySettingRequestedTime =
        fetcher.formData &&
        getOptionalJSONNullStrFormValue(fetcher.formData, "setRequestedTime");
    }

    if (useLoaderOrderInfo) {
      if (unclearedOrderInfo && !actualClearOrder) {
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...unclearedOrderInfo, lines: [] };
        }

        // Get order from api response
        return {
          ...unclearedOrderInfo,
          lines: unclearedOrderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }

      // Generate empty order
      return {
        ...EMPTY_ORDER_RESPONSE,
        orderTypeCode,
        ...(isTableOrdering && { tableNumber: defaultTableNumber }),
        customer: savedCustomer ?? EMPTY_CUSTOMER, // initialise customer from session data only for creating initial order
        requestedReadyTime:
          currentlySettingRequestedTime !== undefined
            ? currentlySettingRequestedTime
            : (requestedReadyTime ?? null),
        deliveryInfo: loaderDeliveryInfo,
        lines: [],
        groupOrder: isTableOrdering,
      };
    }

    // Action submitted but loaders not loaded yet
    // Get data from action response
    if (fetcher.state === "loading") {
      const data: any = fetcher.data;
      if (data?.resOrder) {
        const orderInfo: ResponseOrderWithLines = data.resOrder;
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...orderInfo, lines: [] };
        }
        return {
          ...data.resOrder,
          lines: orderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }
    }

    // Action submitted and currently processing
    // Get order from action request
    const formValue: ResponseOrderWithCartLines = JSON.parse(
      fetcher.formData?.get("orderSummary")?.toString() ?? "",
    );
    return formValue;
  }, [
    menu,
    fetcher.state,
    fetcher.formData,
    fetcher.data,
    unclearedOrderInfo,
    isTableOrdering,
    defaultTableNumber,
    savedCustomer,
    requestedReadyTime,
    loaderDeliveryInfo,
    orderTypeCode,
    clearOrder,
  ]);

  // Cart readonly when time is not valid or if we are currently resetting or inserting a new order, or there are pending payments
  const pendingPayments = !!orderSummary?.pendingPaymentIds?.length;
  const readonlyCart = useMemo(
    () =>
      !timeValid ||
      pendingPayments ||
      (fetcher.state === "submitting" &&
        (!!fetcher.formData?.get("isReset") ||
          !!fetcher.formData?.get("forceInsert"))),
    [fetcher.state, fetcher.formData, timeValid, pendingPayments],
  );

  // Helper function to submit order actions
  const submitOrderAction = ({
    order,
    preventInsert,
    allowSavePersonalData,
    setOrderTypeCode,
    setRequestedTime,
    setDeliveryInfo,
    redirectToPayment,
    errorOnInvalidAsset,
  }: {
    order: ResponseOrderWithCartLines | null;
    preventInsert?: boolean;
    allowSavePersonalData?: boolean;
    setOrderTypeCode?: string;
    setRequestedTime?: string | null;
    setDeliveryInfo?: DeliveryInfo | null;
    redirectToPayment?: boolean;
    errorOnInvalidAsset?: boolean;
  }) => {
    const formData = new FormData();

    let clearCache = false;

    formData.append("orgId", orgId);

    if (errorOnInvalidAsset) {
      formData.append("errorOnInvalidAsset", "true");
    }

    // check for undefined, as null (ASAP) is a valid time to set
    if (setRequestedTime !== undefined) {
      clearCache = true;
      formData.append("setRequestedTime", JSON.stringify(setRequestedTime));
    }

    if (setOrderTypeCode) {
      clearCache = true;
      formData.append("setOrderTypeCode", setOrderTypeCode);
    }

    if (setDeliveryInfo) {
      clearCache = true;
      formData.append("setDeliveryInfo", JSON.stringify(setDeliveryInfo));
    }

    if (preventInsert) {
      formData.append("preventInsert", "true");
    }

    // If the context order has no id, likely we have a new order, or the order in session is no longer valid (e.g. it is already sent)
    if (!order?.id) {
      if (preventInsert) {
        // Insert is prevented, but the order is not allowed to be updated, so we must also prevent update
        formData.append("preventUpdate", "true");
      } else {
        // Force insert a new order since we are not preventing insert
        clearCache = true;
        formData.append("forceInsert", "true");
      }
    }

    if (redirectToPayment) {
      formData.append("redirectToPayment", "true");
    }

    if (order) {
      const orderSummary = recalculateOrder(
        order,
        taxFromCode,
        scheduledPromotions,
      );

      formData.append("orderSummary", JSON.stringify(orderSummary));
    } else {
      clearCache = true;
      formData.append("isReset", "true");

      if (useComoAuth) {
        localStorage.setItem("last-reset", new Date().toISOString());
      }
    }

    if (allowSavePersonalData != undefined) {
      formData.append(
        "allowSavePersonalData",
        allowSavePersonalData.toString(),
      );
    }

    fetcher.submit(formData, {
      action: `/${paramOrderSource}/${site.id}${
        paramTableNumber ? "/" + paramTableNumber : ""
      }?clearCache=${clearCache}`,
      method: "post",
    });
  };

  const setTableNumber = (value: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, tableNumber: value },
    });
  };

  const setTableRef = useRef(setTableNumber);
  setTableRef.current = setTableNumber;
  useEffect(() => {
    if (paramTableNumber && orderSummary.tableNumber !== paramTableNumber) {
      setTableRef.current(paramTableNumber);
    }
  }, [orderSummary.tableNumber, paramTableNumber]);

  const getOrderLineById = (id: string) => {
    return orderSummary.lines.find((orderLine) => orderLine.id === id) || null;
  };

  const addOrderLine = (orderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }

    if (
      !orderLine.item.product.children.length &&
      !orderLine.item.children.length
    ) {
      const combineWith = orderSummary.lines.find(
        (x) =>
          x.item.productCode == orderLine.item.productCode &&
          !x.item.children.length,
      );
      if (combineWith) {
        updateOrderLine({
          ...combineWith,
          item: {
            ...combineWith.item,
            qty: combineWith.item.qty + orderLine.item.qty,
          },
        });
        return;
      }
    }

    const newOrderLines = [...orderSummary.lines, orderLine];
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const removeOrderLine = (id: string) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.filter(
      (orderLine) => orderLine.id !== id,
    );
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const updateOrderLine = (updatedOrderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.map((orderLine) =>
      orderLine.id === updatedOrderLine.id ? updatedOrderLine : orderLine,
    );

    submitOrderAction({
      order: { ...orderSummary, lines: newOrderLines },
    });
  };

  const redeemPromotion = (dealKey: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        redemptionKey: dealKey,
      },
      errorOnInvalidAsset: true,
    });
  };

  const redeemPromotionCode = (code: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        promoCode: code.trim() === "" ? null : code.trim(),
      },
      errorOnInvalidAsset: true,
    });
  };

  const removePromotion = () => {
    submitOrderAction({
      order: { ...orderSummary, redemptionKey: null },
    });
  };

  const removePromotionCode = () => {
    submitOrderAction({
      order: { ...orderSummary, promoCode: null },
    });
  };

  const setCustomerData = (
    customer: BaseUser,
    allowSavePersonalData?: boolean,
  ) => {
    submitOrderAction({
      order: { ...orderSummary, customer },
      allowSavePersonalData,
    });
  };

  const setOrderTypeInfo = (
    newOrderType: OrderType,
    requestedTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    isReset: boolean,
  ) => {
    submitOrderAction({
      // Only set order type if it is new
      setOrderTypeCode:
        newOrderType.code === orderTypeCode ? undefined : newOrderType.code,
      setRequestedTime: requestedTime,
      setDeliveryInfo: deliveryInfo,
      order: isReset
        ? null
        : { ...orderSummary, requestedReadyTime: requestedTime, deliveryInfo },
      // If no order exists, no need to insert one yet, as the requested time is stored in session
      preventInsert: true,
    });
  };

  const reset = () => {
    submitOrderAction({
      order: null,
    });
  };

  const refresh = () => {
    submitOrderAction({
      order: orderSummary,
    });
  };

  const setGroupOrder = (groupOrder: boolean, redirectToPayment: boolean) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, groupOrder },
      redirectToPayment,
    });
  };

  const promotionsRequiringSelection = useMemo(
    () => getPromotionsRequiringSelection(orderSummary),
    [orderSummary],
  );

  const orderType = useMemo(
    () => getOrderType(orderTypeCode, site.orderTypes),
    [orderTypeCode, site.orderTypes],
  );

  // If order is not attached to the authenticated user, detect this and attach it. Only attempt this once however.
  const { authenticatedUser } = useContext(AuthContext);
  const orderRefreshAttempted = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const orderNeedsRefresh =
    !clearOrder &&
    unclearedOrderInfo?.customerId == null &&
    !!authenticatedUser?.id &&
    fetcher.state === "idle";
  useEffect(() => {
    if (!orderRefreshAttempted.current && orderNeedsRefresh) {
      orderRefreshAttempted.current = true;
      console.warn("Order missing customer id. Refreshing...");
      refreshRef.current();
    }
  }, [orderNeedsRefresh]);

  // Get the user to confirm an order time when asap is not available for the selected order type,
  //   and a requested time has not already been set in the session
  const timeNeedsSelecting =
    !searchParams.has("allowImplicitSettings") &&
    orderSummary.id === "" &&
    sessionRequestedTime == null &&
    !asapAvailable;

  const location = useLocation();
  const pathEnd = getRouteEnd(location.pathname);
  const autoShowOrderTypeModal =
    timeNeedsSelecting &&
    !["cancelled", "checkout", "declined", "success", "payment"].includes(
      pathEnd,
    );

  return (
    <OrderContext.Provider
      value={{
        orderSummary,
        orderSource,
        orderTypeCode,
        orderType,
        deliveryInfo: loaderDeliveryInfo,
        isTableOrdering,
        setTableNumber,
        getOrderLineById,
        addOrderLine,
        removeOrderLine,
        updateOrderLine,
        reset,
        refresh,
        setCustomerData,
        redeemPromotion,
        removePromotion,
        redeemPromotionCode,
        removePromotionCode,
        setOrderTypeInfo,
        numTimeSlots: availableTimeSlots.length,
        successPageOrderInfo: unclearedOrderInfo,
        openOrderTimeModal: () => setShowOrderTimeModal(true),
        promotionsRequiringSelection,
        setGroupOrder,
        orderLoading: fetcher.state === "submitting",
        setConfirmingAction,
        readonlyCart,
        asapWait,
        signBackInRequired,
        sessionRequestedTime,
      }}
    >
      {children}
      {/** Change Order modal */}
      <ChangeOrderModal
        visible={showOrderTimeModal || autoShowOrderTypeModal}
        onClose={(isReset, isSave) => {
          if (autoShowOrderTypeModal && !isSave) {
            // Something to hide the modal
            setSearchParams((params) => {
              params.set("allowImplicitSettings", "true");
              return params;
            });
          }

          if (isReset) {
            navigate(
              urlGen("Order", {
                orderSource,
                siteId: site.id,
                menuCode: paramMenuCode,
                tableNumber: paramTableNumber,
              }),
            );
          } else if (isSave && searchParams.has("orderTypeCode")) {
            setSearchParams((params) => {
              params.delete("orderTypeCode");
              return params;
            });
          }
          setShowOrderTimeModal(false);
        }}
      />
      <ConfirmActionModal
        visible={!!confirmingAction}
        onClose={() => setConfirmingAction(null)}
        onConfirm={() => {
          confirmingAction && confirmingAction?.action();
        }}
        icon={confirmingAction?.icon}
        title={confirmingAction?.title}
        body={confirmingAction?.body}
      />
    </OrderContext.Provider>
  );
};

export default OrderContext;
